/**
 * Structured data (JSON-LD) for public publication detail pages.
 * Emits schema.org Book plus BreadcrumbList aligned with `PageBreadcrumb` on the route.
 */

import { env } from '@web/src/lib/env.client'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import {
  plainTextFromRichContent,
  sameAsFromWebLinks as relatedLinkFromWebLinks,
  SEO_META_DESCRIPTION_MAX_CHARS,
  truncateMetaDescription,
} from '@web/src/lib/seo/seo-common'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'
import type { PublicationQuery, ReviewsQuery } from 'types/graphql'

type PublicationForJsonLd = NonNullable<PublicationQuery['publication']>
type ReviewsConnection = ReviewsQuery['reviews']

/**
 * Meta description and JSON-LD `description`: unique per publication; name + optional series/author,
 * then tagline or plain-text excerpt from the long description.
 */
export function buildPublicationDescription(publication: PublicationForJsonLd): string {
  const name = publication.name?.trim() || 'Publication'
  const series = publication.seriesInfo?.trim()
  const author = publication.author?.trim()

  const app = env.VITE_APP_NAME
  let base = `${name} is a lifestyle publication on ${app}.`
  if (series && author) {
    base = `${name} by ${author} is part of the series ${series} on ${app}.`
  } else if (series) {
    base = `${name} is part of the series ${series} on ${app}.`
  } else if (author) {
    base = `${name} by ${author} is a lifestyle publication on ${app}.`
  }

  const tagLine = publication.tagLine?.trim()
  const bodyPlain = plainTextFromRichContent(publication.description ?? undefined)
  const detail = tagLine || bodyPlain
  const out = detail ? `${base} ${detail}` : base

  if (out.length <= SEO_META_DESCRIPTION_MAX_CHARS) return out

  if (detail) {
    const separatorLen = 1
    const budget = SEO_META_DESCRIPTION_MAX_CHARS - base.length - separatorLen
    if (budget >= 40) {
      const truncatedDetail = truncateMetaDescription(detail, budget)
      return `${base} ${truncatedDetail}`
    }
  }

  return truncateMetaDescription(out, SEO_META_DESCRIPTION_MAX_CHARS)
}

function buildPublicationBreadcrumbListJsonLd(
  publicationName: string,
  publicationPageUrl: string
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Discover Cliterature',
        item: absoluteUrlForPath('/publication/list'),
      },
      { '@type': 'ListItem', position: 3, name: publicationName, item: publicationPageUrl },
    ],
  }
}

export function buildPublicationPageJsonLd(input: {
  publication: PublicationForJsonLd
  reviewsConnection: ReviewsConnection | null | undefined
  description: string
}): Record<string, unknown>[] {
  const { publication, reviewsConnection, description: publicationDescription } = input

  const pagePath = `/publication/${publication.slug ?? publication.id}`
  const url = `${baseUrl()}${pagePath}`
  const entityId = `${url}#publication`

  const trimmedDescription = publicationDescription.trim()

  let reviewValue = 0
  if (publication.reviewCount > 0 && publication.reviewSum != null) {
    reviewValue = Math.min(Math.max(0, publication.reviewSum / publication.reviewCount), 5.0)
  }

  const jsonLdReviews =
    reviewsConnection?.entries
      ?.filter((entry) => !entry.markedDeleted && entry.rating != null)
      .map((entry) => {
        const body = entry.content?.trim()
        return {
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: entry.owner?.name ?? 'Anonymous',
          },
          datePublished: entry.createdAt,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: String(entry.rating),
          },
          ...(body ? { reviewBody: body } : {}),
        }
      }) ?? []

  const authorName = publication.author?.trim()

  const mainEntity: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@id': entityId,
    '@type': 'Book',
    name: publication.name,
    ...(trimmedDescription ? { description: trimmedDescription } : {}),
    url,
    ...(authorName
      ? {
          author: {
            '@type': 'Person',
            name: authorName,
          },
        }
      : {}),
    ...(publication.attachedMedia &&
      publication.attachedMedia.length > 0 && {
        image: publication.attachedMedia.slice(0, 5).map((media) => media.imageUrlMedium),
      }),
    ...(reviewValue > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: reviewValue.toFixed(1),
        reviewCount: publication.reviewCount,
        bestRating: '5',
        worstRating: '1',
      },
    }),
    ...(jsonLdReviews.length > 0 && { review: jsonLdReviews }),
    ...(publication.tags &&
      publication.tags.length > 0 && {
        keywords: publication.tags.map((tag) => tag.name).join(', '),
      }),
  }

  const relatedLink = relatedLinkFromWebLinks(publication.webLinks)
  if (relatedLink?.length) {
    mainEntity.relatedLink = relatedLink.length === 1 ? relatedLink[0] : relatedLink
  }

  const breadcrumbList = buildPublicationBreadcrumbListJsonLd(publication.name.trim() || 'Publication', url)

  return [mainEntity, breadcrumbList]
}

/** Loader fields used for `<head>` SEO on the publication detail route. */
export type PublicationPageSeoLoaderInput = {
  publication?: PublicationQuery['publication'] | null
  reviews?: ReviewsQuery['reviews'] | null
}

export function publicationPageHeadFromLoaderData(loaderData: PublicationPageSeoLoaderInput | null | undefined) {
  const publication = loaderData?.publication ?? undefined
  const reviewsConnection = loaderData?.reviews

  if (!publication) {
    return seo({ title: `Publication | ${env.VITE_APP_NAME}` })
  }

  const publicationDescription = buildPublicationDescription(publication)
  const jsonLdData = buildPublicationPageJsonLd({
    publication,
    reviewsConnection,
    description: publicationDescription,
  })

  const titleName = publication.name?.trim() || 'Publication'

  return seo({
    title: `${titleName} | Book | ${env.VITE_APP_NAME}`,
    description: publicationDescription,
    url: `${baseUrl()}/publication/${publication.slug ?? publication.id}`,
    image: publication.attachedMedia?.[0]?.imageUrlMedium,
    author: publication.author?.trim() || publication.owner?.name,
    ogType: 'book',
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd: jsonLdData,
  })
}
